/**
 * Matching a search against names, shared by the results page and the
 * suggestions the browser shows as you type. Case and accents don't count
 * ("atletico" finds "Atlético"); a name that starts with the search comes
 * first, then one with a word that does, then one that merely contains it.
 */

/** Lower case, without accents, with runs of spaces made one. */
export const normalise = (text: string): string =>
  text.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().replace(/\s+/g, " ").trim();

/**
 * How well `names` (a thing's name and any other names it goes by) match an
 * already normalised search: 0 best, 2 worst, undefined for no match.
 */
export function matchRank(normalisedQuery: string, names: readonly string[]): number | undefined {
  if (normalisedQuery === "") return undefined;
  let best: number | undefined;
  for (const name of names) {
    const text = normalise(name);
    const rank = text.startsWith(normalisedQuery)
      ? 0
      : ` ${text}`.includes(` ${normalisedQuery}`)
        ? 1
        : text.includes(normalisedQuery)
          ? 2
          : undefined;
    if (rank !== undefined && (best === undefined || rank < best)) best = rank;
  }
  return best;
}

/**
 * The items matching `query`, best first; ties keep the order they came in.
 *
 * @param namesOf A thing's name and any other names it goes by.
 */
export function search<T>(query: string, items: readonly T[], namesOf: (item: T) => readonly string[]): T[] {
  const wanted = normalise(query);
  return items
    .map((item, index) => ({ item, index, rank: matchRank(wanted, namesOf(item)) }))
    .filter((entry): entry is { item: T; index: number; rank: number } => entry.rank !== undefined)
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((entry) => entry.item);
}
